import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { InputActionMeta } from "react-select";
import { useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";

import { getPreferredContact } from "api";
import { useGetAirports } from "api/queries/common/useGetAirports";
import { useGetSeaports } from "api/queries/common/useGetSeaports";
import { useGetAddressesV2 } from "api/queries/company/useGetAddressesV2";
import { useGetAddressesV2Details } from "api/queries/company/useGetAddressV2Details";
import { companyQueryKeys } from "api/queries/company/companyQueryKeys";
import { userQueryKeys } from "api/queries/user/userQueryKeys";
import { FEATURE_FLAGS } from "constants/featureFlags";
import useDebounce from "hooks/useDebounce";
import useFeatureFlag from "hooks/useFeatureFlag";
import { formatAddressV2SelectOption, buildAddressSelectOptions, buildSyntheticAddressOption } from "@RequestFormV2/sections/Schedule/utils/addressSelectOptions";
import { formatPortOptionLabel } from "@RequestFormV2/sections/Schedule/utils/selectOptionLabel";
import { buildPortSelectOptions, mapSeaportToPortOption } from "@RequestFormV2/sections/Schedule/utils/portSelectOptions";
import { usePersistScheduleDraft } from "@RequestFormV2/hooks/usePersistScheduleDraft";
import { useSchedule } from "@RequestFormV2/sections/Schedule/context";
import { getConsigneeAddressTypeOptions } from "@RequestFormV2/sections/Schedule/utils/scheduleOptions";
import type {
  SharedAddressFieldPaths,
  SharedContactFieldPaths,
  PortOption,
} from "@RequestFormV2/sections/Schedule/utils/sharedAddressFieldPaths";

const ADDRESS_SEARCH_DEBOUNCE_MS = 400;

type ApplyAddressOptions = {
  persist?: boolean;
  applyPreferredContact?: boolean;
};

const mapAirportData = (option: any): PortOption => ({
  id: option.id,
  label: option.name || option.city_name || "",
  value: option.code,
  city_name: option.city_name,
  country_name: option.country_name,
  country_code: option.country_code,
  code: option.code,
  portType: "airport",
  address: option.address,
});

const mapSeaportData = (option: any): PortOption => mapSeaportToPortOption(option);

export interface AddressTypeResolver {
  options: { value: string; label: string }[];
  isAirport: (formValue: unknown) => boolean;
  isSeaport: (formValue: unknown) => boolean;
  formatForRadio: (formValue: unknown) => string;
  parseFromRadio: (radioValue: string) => unknown;
}

export interface UseSharedAddressConfig {
  fieldPaths: SharedAddressFieldPaths;
  contactFieldPaths?: SharedContactFieldPaths;
  fieldsDisabled: boolean;
  isRequired: boolean;
  onAddressSelected?: (address: Record<string, unknown>) => void;
  onPortSelected?: (port: PortOption) => void;
  addressTypeResolver?: AddressTypeResolver;
}

export function useSharedAddress({
  fieldPaths,
  contactFieldPaths,
  fieldsDisabled,
  isRequired,
  onAddressSelected,
  onPortSelected,
  addressTypeResolver,
}: UseSharedAddressConfig) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { getValues, setValue, watch } = useFormContext();
  const { location, scheduleOptions } = useSchedule();
  const persistScheduleDraft = usePersistScheduleDraft();
  const isOptionalPortAddressFormEnabled = useFeatureFlag(FEATURE_FLAGS.FF_WEB10382_OPTIONAL_PORT_ADDRESS_FORM);

  const [searchInput, setSearchInput] = useState("");
  const [isInitialRender, setIsInitialRender] = useState(true);
  const [selectedAddressOption, setSelectedAddressOption] = useState<ReturnType<typeof formatAddressV2SelectOption> | null>(null);

  const debouncedSearchInput = useDebounce(searchInput, ADDRESS_SEARCH_DEBOUNCE_MS);

  const defaultOptions = useMemo(() => getConsigneeAddressTypeOptions(intl, scheduleOptions), [intl, scheduleOptions]);

  const addressTypeOptions = addressTypeResolver?.options ?? defaultOptions;

  const addressTypeRadioOptions = useMemo(
    () =>
      addressTypeOptions.map((option) => ({
        value: option.value,
        label: option.label,
      })),
    [addressTypeOptions],
  );

  const addressTypeFormValue = watch(fieldPaths.address_type);
  const addressId = watch(fieldPaths.address_id) as number | undefined;
  const addressReferenceNumber = watch(fieldPaths.address_reference_number);
  const addressStreet = watch(fieldPaths.address_street);
  const addressStreetLine2 = watch(fieldPaths.address_street_line2);
  const addressName = watch(fieldPaths.address_name);
  const cityName = watch(fieldPaths.address_city);
  const stateName = watch(fieldPaths.address_state);
  const countryName = watch(fieldPaths.address_country);
  const zipcode = watch(fieldPaths.address_zipcode);

  const isAirport = addressTypeResolver
    ? addressTypeResolver.isAirport(addressTypeFormValue)
    : addressTypeFormValue === "airport";
  const isSeaport = addressTypeResolver
    ? addressTypeResolver.isSeaport(addressTypeFormValue)
    : addressTypeFormValue === "seaport";
  const showPortField = isAirport || isSeaport;

  const formatForRadio = addressTypeResolver?.formatForRadio ?? ((v: unknown) => v as string);
  const parseFromRadio = addressTypeResolver?.parseFromRadio ?? ((v: string) => v);

  const isOptionalAirportOrSeaport =
    isOptionalPortAddressFormEnabled && (isAirport || isSeaport);

  const hasSelectedAddress = Boolean(addressName || addressStreet);

  const selectedAddressSummary = useMemo(
    () => ({
      street: addressStreet as string | undefined,
      cityName: cityName as string | undefined,
      stateName: stateName as string | undefined,
      zipcode: zipcode as string | undefined,
      countryName: countryName as string | undefined,
      hasContent: hasSelectedAddress,
    }),
    [addressStreet, cityName, stateName, zipcode, countryName, hasSelectedAddress],
  );

  const portPlaceholder = useMemo(
    () =>
      isAirport
        ? intl.formatMessage({
            id: "new-request.schedule.autoCompletePlaceholder.searchAirport",
            defaultMessage: "Search airport",
          })
        : intl.formatMessage({
            id: "new-request.schedule.autoCompletePlaceholder.searchPort",
            defaultMessage: "Search port",
          }),
    [intl, isAirport],
  );

  const portFieldName = isAirport ? fieldPaths.airport_name : fieldPaths.seaport_name;
  const portCodeFieldName = isAirport ? fieldPaths.airport_code : fieldPaths.seaport_code;
  const portCode = watch(portCodeFieldName) as string | undefined;
  const portName = watch(portFieldName) as string | undefined;

  const [airportQuery, setAirportQuery] = useState("");
  const [seaportQuery, setSeaportQuery] = useState("");
  const [selectedPortOption, setSelectedPortOption] = useState<PortOption | null>(null);

  const { data: airportsRaw = [], isFetching: isFetchingAirports } = useGetAirports(airportQuery, {
    enabled: isAirport && !fieldsDisabled,
  });

  const { data: seaportsRaw = [], isFetching: isFetchingSeaports } = useGetSeaports(seaportQuery, {
    enabled: isSeaport && !fieldsDisabled,
  });

  const portOptions = useMemo((): PortOption[] => {
    const mapped = isAirport
      ? airportsRaw.map((option) => mapAirportData(option))
      : seaportsRaw.map((option) => mapSeaportData(option));

    return buildPortSelectOptions({
      mapped,
      portCode,
      portName,
      selectedPortOption,
      isAirport,
    });
  }, [isAirport, airportsRaw, seaportsRaw, portCode, portName, selectedPortOption]);

  const formatPortOptionLabelForSelect = useCallback(
    (option: PortOption, meta: Parameters<typeof formatPortOptionLabel>[1]) => {
      const resolved = portOptions.find((portOption) => portOption.value === option.value) ?? option;
      return formatPortOptionLabel(resolved, meta);
    },
    [portOptions],
  );

  const isLoadingPorts = isAirport ? isFetchingAirports : isFetchingSeaports;

  const { data: addresses = [], isFetching: isFetchingAddresses, refetch: refetchAddressesQuery } = useGetAddressesV2(
    {
      params: {
        query: debouncedSearchInput,
        payment_location: location,
      },
    },
    {
      enabled: Boolean(location) && !fieldsDisabled,
      keepPreviousData: true,
    },
  );

  const { data: addressV2Details } = useGetAddressesV2Details(addressReferenceNumber, {
    options: {
      enabled: Boolean(!addressStreet && addressReferenceNumber && !fieldsDisabled),
      refetchOnMount: true,
    },
  });

  const setAddressFromSelection = useCallback(
    (address: Record<string, unknown>, options?: ApplyAddressOptions) => {
      if (!address || fieldsDisabled) return;

      const currentId = getValues(fieldPaths.address_id) as number | undefined;
      setValue(fieldPaths.address_id, (address.id as number | undefined) ?? currentId);
      setValue(fieldPaths.address_name, (address.name ?? address.label) as string);
      setValue(fieldPaths.address_street, (address.street_address as string) ?? "");
      setValue(fieldPaths.address_street_line2, (address.street_address_line_two as string) ?? "");
      setValue(fieldPaths.address_city, (address.city_name as string) ?? "");
      setValue(fieldPaths.address_state, (address.state_name as string) ?? "");
      setValue(fieldPaths.address_country, (address.country_name as string) ?? "");
      setValue(fieldPaths.address_zipcode, (address.zipcode as string) ?? "");
      setValue(fieldPaths.address_reference_number, (address.reference_number as string) ?? "");

      setSelectedAddressOption(formatAddressV2SelectOption(address as any));

      onAddressSelected?.(address);

      const selectedRef = address.reference_number as string | undefined;
      if (options?.applyPreferredContact && selectedRef && contactFieldPaths) {
        queryClient
          .fetchQuery({
            queryKey: userQueryKeys.preferredContact(selectedRef),
            queryFn: () => getPreferredContact(selectedRef).then((res) => res.data),
          })
          .then((preferredContact) => {
            const currentRef = getValues(fieldPaths.address_reference_number);
            if (currentRef !== selectedRef || fieldsDisabled) return;

            setValue(contactFieldPaths.contact_name, preferredContact.name);
            setValue(contactFieldPaths.contact_phone, preferredContact.phone_number);
            setValue(contactFieldPaths.contact_email, preferredContact.email);
            if (options?.persist) {
              persistScheduleDraft();
            }
          })
          .catch(() => {});
      }

      if (options?.persist) {
        persistScheduleDraft();
      }
    },
    [
      fieldsDisabled,
      getValues,
      queryClient,
      setValue,
      fieldPaths,
      contactFieldPaths,
      onAddressSelected,
      persistScheduleDraft,
    ],
  );

  useEffect(() => {
    if (!addressV2Details || fieldsDisabled || addressStreet) return;
    setAddressFromSelection(addressV2Details as unknown as Record<string, unknown>);
  }, [addressV2Details, setAddressFromSelection, fieldsDisabled, addressStreet]);

  const mappedAddressOptions = useMemo(() => {
    if (addressV2Details && isInitialRender) {
      return [formatAddressV2SelectOption(addressV2Details as any)];
    }
    return addresses?.map((addr: any) => formatAddressV2SelectOption(addr)) ?? [];
  }, [addressV2Details, isInitialRender, addresses]);

  const fallbackAddressOption = useMemo(() => {
    if (!addressReferenceNumber) return null;
    return buildSyntheticAddressOption({
      id: addressId,
      name: addressName as string | undefined,
      street_address: addressStreet as string | undefined,
      street_address_line_two: addressStreetLine2 as string | undefined,
      city_name: cityName as string | undefined,
      state_name: stateName as string | undefined,
      country_name: countryName as string | undefined,
      zipcode: zipcode as string | undefined,
      reference_number: addressReferenceNumber as string,
    });
  }, [
    addressReferenceNumber,
    addressId,
    addressName,
    addressStreet,
    addressStreetLine2,
    cityName,
    stateName,
    countryName,
    zipcode,
  ]);

  const resolvedFallbackOption = useMemo(() => {
    if (!addressReferenceNumber) return null;
    if (fallbackAddressOption) return fallbackAddressOption;
    if (
      selectedAddressOption &&
      (selectedAddressOption.value === addressReferenceNumber ||
        selectedAddressOption.reference_number === addressReferenceNumber)
    ) {
      return selectedAddressOption;
    }
    return null;
  }, [addressReferenceNumber, fallbackAddressOption, selectedAddressOption]);

  const addressSelectOptions = useMemo(
    () =>
      buildAddressSelectOptions({
        mapped: mappedAddressOptions,
        addressReferenceNumber: addressReferenceNumber as string | undefined,
        fallbackOption: resolvedFallbackOption,
      }),
    [mappedAddressOptions, addressReferenceNumber, resolvedFallbackOption],
  );

  const refetchAddresses = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: companyQueryKeys.addressesV2({})[0] });
    refetchAddressesQuery();
  }, [queryClient, refetchAddressesQuery]);

  const handleDialogAddressSuccess = useCallback(
    (data: Record<string, unknown>) => {
      if (!data) return;
      const currentId = getValues(fieldPaths.address_id) as number | undefined;
      setAddressFromSelection(
        {
          ...data,
          id: (data.id as number | undefined) ?? currentId,
          name: data.name,
        },
        { persist: true, applyPreferredContact: true },
      );
      setIsInitialRender(false);
    },
    [getValues, fieldPaths.address_id, setAddressFromSelection],
  );

  const clearAddressFields = useCallback(
    (options?: { persist?: boolean }) => {
      if (fieldsDisabled) return;
      setValue(fieldPaths.address_reference_number, "");
      setValue(fieldPaths.address_id, undefined);
      setValue(fieldPaths.address_name, "");
      setValue(fieldPaths.address_street, "");
      setValue(fieldPaths.address_street_line2, "");
      setValue(fieldPaths.address_city, "");
      setValue(fieldPaths.address_state, "");
      setValue(fieldPaths.address_country, "");
      setValue(fieldPaths.address_zipcode, "");
      setSelectedAddressOption(null);
      if (options?.persist) {
        persistScheduleDraft();
      }
    },
    [setValue, fieldPaths, fieldsDisabled, persistScheduleDraft],
  );

  const handleAddressTypeChange = useCallback(
    (radioValue: string, fieldOnChange: (value: unknown) => void) => {
      if (fieldsDisabled) return;
      fieldOnChange(parseFromRadio(radioValue));

      setValue(fieldPaths.airport_code, "");
      setValue(fieldPaths.airport_name, "");
      setValue(fieldPaths.seaport_code, "");
      setValue(fieldPaths.seaport_name, "");
      setSelectedPortOption(null);

      if (isOptionalPortAddressFormEnabled) {
        clearAddressFields();
      }

      setValue(fieldPaths.address_id, undefined);
      setValue(fieldPaths.address_name, "");
    },
    [
      setValue,
      fieldPaths,
      isOptionalPortAddressFormEnabled,
      clearAddressFields,
      fieldsDisabled,
      parseFromRadio,
    ],
  );

  const handleAddressInputChange = useCallback(
    (val: string, meta: InputActionMeta) => {
      if (fieldsDisabled) return;

      if (meta.action === "clear" || (meta.action === "set-value" && !val)) {
        setSearchInput("");
        return;
      }

      if (meta.action !== "input-change") return;

      if (!val?.trim()) {
        setValue(fieldPaths.address_reference_number, "");
        setValue(fieldPaths.address_id, undefined);
        setValue(fieldPaths.address_name, "");
      }

      setSearchInput(val);
      setIsInitialRender(false);
    },
    [setValue, fieldPaths, fieldsDisabled],
  );

  const handleAddressSelect = useCallback(
    (val: string | null) => {
      if (fieldsDisabled) return;

      if (!val) {
        clearAddressFields({ persist: true });
        setSearchInput("");
        return;
      }

      const selected = (addressSelectOptions ?? []).find((opt) => opt.value === val);
      if (selected) {
        setAddressFromSelection(selected as Record<string, unknown>, {
          persist: true,
          applyPreferredContact: true,
        });
      }
    },
    [addressSelectOptions, setAddressFromSelection, fieldsDisabled, clearAddressFields],
  );

  const handleFillPort = useCallback(
    (port: PortOption) => {
      if (!port || fieldsDisabled) return;

      if (isSeaport) {
        setValue(fieldPaths.seaport_name, port.city_name || port.label);
        setValue(fieldPaths.seaport_code, port.code);
      } else if (isAirport) {
        setValue(fieldPaths.airport_name, port.city_name || port.label);
        setValue(fieldPaths.airport_code, port.code);
      }

      onPortSelected?.(port);
      persistScheduleDraft();
    },
    [isAirport, isSeaport, setValue, fieldPaths, fieldsDisabled, onPortSelected, persistScheduleDraft],
  );

  const handlePortSelect = useCallback(
    (val: string | null) => {
      if (fieldsDisabled) return;

      if (!val) {
        setValue(portFieldName, "");
        setValue(portCodeFieldName, "");
        setSelectedPortOption(null);
        if (isAirport) {
          setAirportQuery("");
        } else {
          setSeaportQuery("");
        }
        if (
          fieldPaths.address_type.startsWith("schedule.consignee.") ||
          fieldPaths.address_type.startsWith("schedule.notify_party.")
        ) {
          persistScheduleDraft();
        }
        return;
      }

      const selected = portOptions.find((opt) => opt.value === val);
      if (selected) {
        setSelectedPortOption(selected);
        handleFillPort(selected);
      }
    },
    [
      portOptions,
      handleFillPort,
      fieldsDisabled,
      setValue,
      portFieldName,
      portCodeFieldName,
      isAirport,
      persistScheduleDraft,
      fieldPaths.address_type,
    ],
  );

  const handlePortInputChange = useCallback(
    (val: string) => {
      if (fieldsDisabled) return;
      if (isAirport) {
        setAirportQuery(val);
      } else {
        setSeaportQuery(val);
      }
    },
    [isAirport, fieldsDisabled],
  );

  const addressFieldsRequired = isRequired && !fieldsDisabled;

  return {
    fieldPaths,
    formatForRadio,
    addressTypeOptions,
    addressTypeRadioOptions,
    addressSelectOptions,
    isFetchingAddresses,
    isOptionalAirportOrSeaport,
    selectedAddressSummary,
    fieldsDisabled,
    addressFieldsRequired,
    handleAddressTypeChange,
    handleAddressSelect,
    handleAddressInputChange,
    handleDialogAddressSuccess,
    refetchAddresses,
    showPortField,
    isAirport,
    portFieldName,
    portCodeFieldName,
    portOptions,
    isLoadingPorts,
    portPlaceholder,
    formatPortOptionLabelForSelect,
    handlePortInputChange,
    handlePortSelect,
  };
}

export type SharedAddressSlice = ReturnType<typeof useSharedAddress>;
